import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ChartBarIcon,
  BeakerIcon,
  CubeIcon,
  DocumentTextIcon,
  CubeTransparentIcon,
  BuildingStorefrontIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState({
    ingredients: 0,
    packaging: 0,
    recipes: 0,
    products: 0,
    channels: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [ingredientsRes, packagingRes, recipesRes, productsRes, channelsRes] = await Promise.all([
        axios.get(`${API}/ingredients`),
        axios.get(`${API}/packaging`),
        axios.get(`${API}/recipes`),
        axios.get(`${API}/products`),
        axios.get(`${API}/channels`)
      ]);

      setStats({
        ingredients: ingredientsRes.data.length,
        packaging: packagingRes.data.length,
        recipes: recipesRes.data.length,
        products: productsRes.data.length,
        channels: channelsRes.data.length
      });
    } catch (error) {
      console.error('Errore nel caricamento delle statistiche:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      name: 'Ingredienti',
      value: stats.ingredients,
      icon: BeakerIcon,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      change: '+12%',
      changeType: 'increase'
    },
    {
      name: 'Componenti Packaging',
      value: stats.packaging,
      icon: CubeIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      change: '+8%',
      changeType: 'increase'
    },
    {
      name: 'Ricette',
      value: stats.recipes,
      icon: DocumentTextIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      change: '+5%',
      changeType: 'increase'
    },
    {
      name: 'Prodotti',
      value: stats.products,
      icon: CubeTransparentIcon,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      change: '+3%',
      changeType: 'increase'
    },
    {
      name: 'Canali di Vendita',
      value: stats.channels,
      icon: BuildingStorefrontIcon,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      change: '+2%',
      changeType: 'increase'
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gray-200 rounded"></div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate" data-testid="dashboard-title">
            Dashboard Sistema Margini
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Panoramica generale del sistema di analisi margini per prodotti vinicoli
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="card slide-up" data-testid={`stat-card-${stat.name.toLowerCase()}`}>
              <div className="card-body">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">
                          {stat.value}
                        </div>
                        <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                          stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {stat.changeType === 'increase' ? (
                            <ArrowUpIcon className="self-center flex-shrink-0 h-3 w-3" />
                          ) : (
                            <ArrowDownIcon className="self-center flex-shrink-0 h-3 w-3" />
                          )}
                          <span className="sr-only">
                            {stat.changeType === 'increase' ? 'Increased' : 'Decreased'} by
                          </span>
                          {stat.change}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Attività Recenti</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="text-center py-8 text-gray-500">
                <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2">Nessuna attività recente</p>
                <p className="text-sm">Le attività appariranno qui una volta iniziato l'inserimento dei dati</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Start Guide */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Guida Rapida</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                    1
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Crea Ingredienti</p>
                  <p className="text-sm text-gray-500">Inizia definendo gli ingredienti base per le tue ricette</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                    2
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Aggiungi Packaging</p>
                  <p className="text-sm text-gray-500">Definisci i componenti di packaging (bottiglie, tappi, etichette)</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                    3
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Crea Ricette</p>
                  <p className="text-sm text-gray-500">Componi le ricette utilizzando gli ingredienti definiti</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                    4
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Configura Prodotti</p>
                  <p className="text-sm text-gray-500">Combina ricette e packaging per creare i prodotti finali</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                    5
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Analizza Margini</p>
                  <p className="text-sm text-gray-500">Visualizza i margini operativi per prodotto e canale</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;